import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  DoctorProfile,
  PatchDoctorDetailsInput,
  PutDoctorProfileInput,
} from './types';

const key = (staffId: string) => ['doctor-profile', staffId] as const;

/** GET /admin/staff/{id}/doctor-profile (RL-905, RL-1175). */
export function useDoctorProfile(staffId: string) {
  return useQuery({
    queryKey: key(staffId),
    queryFn: ({ signal }) =>
      api
        .get<DoctorProfile>(`/admin/staff/${staffId}/doctor-profile`, {
          signal,
        })
        .then((r) => r.data),
  });
}

/** PUT /admin/staff/{id}/doctor-profile: replaces qualifications and photo. */
export function useSaveDoctorProfile(staffId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PutDoctorProfileInput) =>
      api
        .put<DoctorProfile>(`/admin/staff/${staffId}/doctor-profile`, body)
        .then((r) => r.data),
    onSuccess: (saved) => qc.setQueryData(key(staffId), saved),
  });
}

/** RL-1791: PATCH /admin/staff/{id}/doctor-details (name, email, MMC, APC). */
export function useSaveDoctorDetails(staffId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PatchDoctorDetailsInput) =>
      api
        .patch<DoctorProfile>(`/admin/staff/${staffId}/doctor-details`, body)
        .then((r) => r.data),
    onSuccess: (saved) => {
      qc.setQueryData(key(staffId), saved);
      void qc.invalidateQueries({ queryKey: ['admin', 'staff'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'rosters'] });
    },
  });
}

/** Upload or remove the approved photo independently of qualifications. */
export function useDoctorPhoto(staffId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (photo: string | null) =>
      photo === null
        ? api
            .del<DoctorProfile>(`/admin/staff/${staffId}/doctor-profile/photo`)
            .then((r) => r.data)
        : api
            .put<DoctorProfile>(
              `/admin/staff/${staffId}/doctor-profile/photo`,
              { photo },
            )
            .then((r) => r.data),
    onSuccess: (saved) => qc.setQueryData(key(staffId), saved),
  });
}
